import { AppState, AppStateStatus } from "react-native"

import { t } from "i18next"

import { Event as AuthEvent } from "@treefort/lib/authenticator"
import { runCancellable } from "@treefort/lib/cancellable"
import { DisplayableError } from "@treefort/lib/displayable-error"
import { AudioError } from "@treefort/lib/errors"

import { progressStore } from "../watermelon/stores/progress"
import { syncManager } from "../watermelon/sync"
import analytics from "./analytics"
import {
  audioPlayer,
  getDefaultPlaybackRate,
  getLoopMode,
  setLoopMode,
} from "./audio-player"
import authenticator from "./authenticator"
import { isPlaying } from "./av/playback"
import {
  getConsumableContentFromContentResponse,
  getTracksFromConsumableContent,
  isConsumableContentAvailable,
  PlayableContent,
} from "./consumable-content"
import { getContent } from "./content"
import DownloadItem from "./download-item"
import { logError } from "./logging"
import { getNetworkState, isOfflineState } from "./network-state"
import { addActiveProfileListener, getActiveProfile } from "./profiles"
import {
  PlayableProgressItemData,
  PlayableProgressItem,
  ProgressItem,
  getProgressItemKeyFromConsumableContent,
  fetchProgressForConsumableContent,
  getProgressItemKey,
  fetchProgress,
  getProgressItemKeyFromTrack,
} from "./progress-item"

// This represents the data needed to load or play content audio
type ContentAudioData = {
  consumableContent: PlayableContent
  profileId: string | null
  trackId?: string
}

const noop = (): void => {}
let cancelLoadAndSyncAudio: () => void = noop
export let cancelPlayContentAudio: () => void = noop

/**
 * Load audio from content and sync progress but don't start playing.
 */
async function* loadContentAudioGenerator(
  data: ContentAudioData,
): AsyncGenerator<unknown> {
  const { consumableContent, trackId, profileId } = data
  const downloadItem = new DownloadItem({ consumableContent })
  const [networkState, downloadItemState] = await Promise.all([
    getNetworkState(),
    downloadItem.getState(),
  ])
  yield // Allow cancellation after fetching network state and download item state
  if (isOfflineState(networkState) && downloadItemState.type !== "downloaded") {
    throw new DisplayableError(t("Cannot play audio - no network connection."))
  }

  const progressItem = await fetchProgressForConsumableContent({
    consumableContent,
    profileId,
  })
  yield // Allow cancellation after loading progress

  // Use content from the download item for offline playback
  const tracks = getTracksFromConsumableContent({
    consumableContent: await downloadItem.getOfflineConsumableContent(),
    profileId,
  })
  yield // Allow cancellation after fetching downloaded content

  yield audioPlayer.pause()
  if (tracks.length === 0) {
    throw new DisplayableError(
      t("Please update your app to access this content."),
      `[Content] No compatible tracks found for content "${consumableContent.content.id}".`,
    )
  }

  const progress = progressItem.getProgress()
  const progressTrack = progressItem.getTrack(tracks)

  yield audioPlayer.setTracks(tracks, {
    trackId: progress.finished ? tracks[0]?.id : trackId ?? progressTrack?.id,
    position: progress.finished || trackId ? 0 : progress.position,
    playbackRate:
      consumableContent.type === "album"
        ? 1
        : progressItem?.hasPlaybackRate()
          ? progressItem.getPlaybackRate()
          : (await getDefaultPlaybackRate({
              contentId: consumableContent.content.id,
              contentType: consumableContent.content.type,
              profileId,
            })) || 1,
    skipMode: consumableContent.type === "album" ? "track" : "position",
    loopMode: consumableContent.type === "album" ? await getLoopMode() : "off",
  })

  // Save the ProgressItem key of the audio that is currently loaded into the
  // audio player so that we can re-load it on app startup.
  await progressStore
    .saveCurrentAudioProgressItemKey({
      profileId,
      value: progressItem.getKey(),
    })
    .catch(logError)

  return progressItem
}

export const loadContentAudio = (
  data: ContentAudioData,
): Promise<PlayableProgressItem | undefined> => {
  cancelLoadAndSyncAudio()
  const { cancel, promise } = runCancellable(loadContentAudioGenerator(data))
  cancelLoadAndSyncAudio = cancel
  return promise
}

/**
 * Load audio from content and sync progress and start playing. Does nothing if
 * the content is already playing.
 */
async function* playContentAudioGenerator(
  data: ContentAudioData,
): AsyncGenerator {
  try {
    audioPlayer.publishPlayIntent()
    const tracks = getTracksFromConsumableContent(data)
    const audioState = audioPlayer.getPlaybackState()
    const progressKey = getProgressItemKeyFromConsumableContent(
      data.consumableContent,
    )

    // Skip if the requested content is already playing
    const currentTrack = audioPlayer.getTrack()
    if (
      isPlaying(audioState) &&
      !data.trackId &&
      currentTrack &&
      progressKey === getProgressItemKey(currentTrack.extra)
    ) {
      return
    }

    // If no compatible tracks were found then assume an update is required
    if (tracks.length === 0) {
      throw new DisplayableError(
        t("Please update your app to access this content."),
        `[Content] No compatible tracks found for content "${data.consumableContent.content.id}". Client is likely outdated.`,
      )
    }

    // If we're playing an album and our last saved loop mode was set to "one"
    // then revert to "all". The thinking is that if a user is switching tracks
    // he's likely wanting to move out of loop-one mode.
    if (data.consumableContent.type === "album") {
      const loopMode = await getLoopMode()
      yield
      if (loopMode === "one") {
        yield setLoopMode("all")
      }
    }

    const progressItem = await loadContentAudio(data)

    if (progressItem) {
      yield audioPlayer.play()
      yield analytics.logPlayRequest({ tracks, progressItem })
    }
  } catch (error) {
    logError(error)
  }
}

export function playContentAudio(data: ContentAudioData): Promise<void> {
  cancelPlayContentAudio()
  const { cancel, promise } = runCancellable(playContentAudioGenerator(data))
  cancelPlayContentAudio = cancel
  return promise.catch(logError)
}

export async function stopAudioPlayer({
  profileId,
}: {
  profileId: string | null
}) {
  await Promise.all([
    audioPlayer.stop(),
    progressStore.clearCurrentAudioProgressItemKey({ profileId }),
  ]).catch((cause) =>
    logError(new Error("Failed to stop audio player", { cause })),
  )
}

export async function stopAudioPlayerIfCurrent(progressItem: ProgressItem) {
  const profileId = progressItem.getProfileId()
  const currentTrack = audioPlayer.getTrack()
  if (
    currentTrack &&
    progressItem.getKey() === getProgressItemKey(currentTrack.extra)
  ) {
    await stopAudioPlayer({ profileId })
  }
}

/**
 * Load the last played content audio into the player. If we have no record of
 * what was played last, close the player.
 */
async function loadLastPlayedContentAudio(profileId: string | null) {
  try {
    await syncManager.requestSync({ syncType: "immediate" })
    const currentAudioProgressKey =
      await progressStore.getCurrentAudioProgressItemKey({
        profileId,
      })
    const rawProgressData = currentAudioProgressKey
      ? await progressStore.get(currentAudioProgressKey, { profileId })
      : undefined
    const progressData = rawProgressData
      ? (JSON.parse(rawProgressData) as PlayableProgressItemData)
      : undefined
    let lastPlayedContentAudio: ContentAudioData | undefined
    if (progressData) {
      const content = await getContent(progressData.contentId)
      const consumableContent =
        content?.type === "podcast" ||
        content?.type === "book" ||
        content?.type === "video" ||
        content?.type === "album"
          ? getConsumableContentFromContentResponse({
              content,
              podcastEpisodeNumber:
                progressData.contentType === "podcast"
                  ? progressData.podcastEpisode
                  : undefined,
            })
          : undefined
      lastPlayedContentAudio =
        consumableContent && isConsumableContentAvailable(consumableContent)
          ? { consumableContent, profileId }
          : undefined
    }
    if (lastPlayedContentAudio) {
      await loadContentAudio(lastPlayedContentAudio)
    } else {
      await audioPlayer.stop()
    }
  } catch (cause) {
    logError(new AudioError("Failed to load the last played audio", { cause }))
  }
}

/**
 * This function sets up a host of event listeners to coordinate behavior
 * related to the audio player and content.
 */
function addEventListeners() {
  // Load the last played audio any time the user signs in
  authenticator.on(AuthEvent.User, async (user) => {
    if (user) {
      const profile = await getActiveProfile()
      await loadLastPlayedContentAudio(profile.id ?? null)
    }
  })

  // Load the last played audio any time the user profile changes. If we have no
  // record of the last played audio, stop the player.
  addActiveProfileListener(async (profile) => {
    if (profile.state !== "loading") {
      await loadLastPlayedContentAudio(
        profile.state === "set" ? profile.id : null,
      )
    }
  })

  // If the app moves from the background to the foreground and the audio player
  // is paused then fetch progress for the item loaded in the player and skip to
  // the correct track/position
  let previousAppState: AppStateStatus | undefined = undefined
  AppState.addEventListener("change", async (appState) => {
    try {
      const trackFromPlayer = audioPlayer.getTrack()
      const positionFromPlayer = audioPlayer.getProgress().position
      if (
        appState === "active" &&
        previousAppState === "background" &&
        !isPlaying(audioPlayer.getPlaybackState()) &&
        trackFromPlayer
      ) {
        await syncManager.requestSync({ syncType: "immediate" })
        const profile = await getActiveProfile()
        const progressItem = await fetchProgress<PlayableProgressItem>({
          key: getProgressItemKeyFromTrack(trackFromPlayer),
          profileId: profile.id || null,
        })

        const trackFromProgress = progressItem?.getTrack(
          audioPlayer.getTracks(),
        )
        const positionFromProgress = progressItem?.getData().progress?.position

        // If saved progress indicates that we should skip to a different
        // position on the same track, do so as long as the user hasn't started
        // playback while we were thinking.
        if (
          !isPlaying(audioPlayer.getPlaybackState()) &&
          trackFromProgress?.id === trackFromPlayer.id &&
          positionFromProgress &&
          // NOTE: Ignore position differences less than 5 seconds. A difference
          // this small is within the margin of error when scrubbing through a
          // remote stream does not justify triggering buffering/loading and
          // reducing the TTI for the audio player.
          Math.abs(positionFromProgress - positionFromPlayer) >= 5000
        ) {
          await audioPlayer.skipToPosition(positionFromProgress)
        }
        // If saved progress indicates that we should skip to a new track
        // entirely, do so as long as the user hasn't started playback while we
        // were thinking.
        else if (
          !isPlaying(audioPlayer.getPlaybackState()) &&
          trackFromProgress &&
          trackFromProgress.id !== trackFromPlayer.id
        ) {
          await audioPlayer.skipToTrack(trackFromProgress.id, {
            position: positionFromProgress,
          })
        }
      }
    } finally {
      previousAppState = appState
    }
  })
}

addEventListeners()
